import { ArrowDownIcon, ArrowUpIcon, PlusIcon, XIcon } from 'lucide-react';
import { useStableKeys } from '../../hooks/useStableKeys';
import { undoToast } from './undoToast';
import { Field } from './Field';
import { fieldLabels } from '../adminLabels';
import { btnIcon, inputCls } from './styles';
import { move } from '../editing';
import { ContentEditor } from './ContentEditor';
import { LONG_TEXT, emptied, rowsFor, tint, titleOf, type Json } from './contentShape';

/** De vorm van een nieuwe regel als een lijst van kaarten helemaal leeg is (anders: de eerste regel, leeggemaakt). */
const EMPTY_LIST_TEMPLATES: Record<string, Json> = {
  picks: {
    pickId: '',
    why: ''
  }
};
type Props = {
  fieldKey: string;
  label: string;
  value: Json[];
  onChange: (v: Json) => void;
  depth: number;
};

/**
 * Een lijst in de pagina-editor: losse regels (bijv. "inbegrepen") of kaarten (bijv. stappen,
 * citaten). Verplaatsen, verwijderen (met ongedaan maken) en toevoegen in dezelfde vorm.
 */
export function ContentListField({
  fieldKey,
  label,
  value,
  onChange,
  depth
}: Props) {
  const meta = fieldLabels[fieldKey];
  const simple = value.every(v => typeof v === 'string');
  const template: Json = value.length ? emptied(value[0] ?? '') : EMPTY_LIST_TEMPLATES[fieldKey] ?? '';
  const set = (i: number, next: Json) => onChange(value.map((v, j) => j === i ? next : v));

  // Vaste sleutels per rij (los van de positie), zodat focus en invoer bij de juiste rij
  // blijven na verplaatsen of verwijderen. Houdt de lengte gelijk als de lijst van buitenaf wijzigt.
  const rows = useStableKeys(value.length);
  const reorder = (from: number, to: number) => {
    rows.move(from, to);
    onChange(move(value, from, to));
  };
  const remove = (i: number) => {
    const before = value;
    const beforeIds = rows.snapshot();
    const name = titleOf(value[i] ?? '');
    rows.remove(i);
    onChange(value.filter((_, j) => j !== i));
    // Eén klik op het kruisje wist soms een heel citaat of een aflevering: altijd terug te zetten.
    undoToast(name ? `“${name.slice(0, 40)}${name.length > 40 ? '…' : ''}” verwijderd` : 'Verwijderd', () => {
      rows.restore(beforeIds);
      onChange(before);
    });
  };
  const controls = (i: number) => {
    // Schermlezers horen welke regel het is: "Veld 2 van 5 omhoog".
    const name = `${label || 'Regel'} ${i + 1} van ${value.length}`;
    return <span className="flex shrink-0 items-center justify-end" data-id="element-4943" data-mp="1c2kh0t50kiap">
        <button type="button" onClick={() => reorder(i, i - 1)} disabled={i === 0} aria-label={`${name} omhoog`} className={btnIcon} data-id="element-4944" data-mp="1c19bans0d0gy">
          <ArrowUpIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-4945" data-mp="1325pvd5dc8za" />
        </button>
        <button type="button" onClick={() => reorder(i, i + 1)} disabled={i === value.length - 1} aria-label={`${name} omlaag`} className={btnIcon} data-id="element-4946" data-mp="1737qnyexot3d">
          <ArrowDownIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-4947" data-mp="2xgf0tnp7vx9i" />
        </button>
        <button type="button" onClick={() => remove(i)} aria-label={`${name} verwijderen`} className={btnIcon} data-id="element-4948" data-mp="31gpi3wigma3o">
          <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4949" data-mp="ps2lj0rtdgd9" />
        </button>
      </span>;
  };
  return <Field label={label} hint={meta?.hint} as="div" data-id="element-4950" data-mp="zjorahldun8m">
      {simple ? <ul className="space-y-2" data-id="element-4951" data-mp="zm1illd169yc">
          {value.map((v, i) => <li key={rows.keys[i]} className="flex flex-col gap-1 sm:flex-row sm:items-start" data-id="element-4952" data-mp="usgjfpzpb9xk" data-mpi={i}>
              {meta?.long || String(v).length > LONG_TEXT ? <textarea value={String(v)} onChange={e => set(i, e.target.value)} rows={rowsFor(String(v))} aria-label={`${label} ${i + 1}`} className={`${inputCls} resize-y leading-relaxed`} data-id="element-4953" data-mp="14jkvagu2yeo6" /> : <input value={String(v)} onChange={e => set(i, e.target.value)} aria-label={`${label} ${i + 1}`} className={inputCls} data-id="element-4954" data-mp="3kb8p43dsrr2r" />}
              {controls(i)}
            </li>)}
        </ul> : <ul className="space-y-3" data-id="element-4955" data-mp="3hphpg2r4ubmr">
          {value.map((v, i) => <li key={rows.keys[i]} className={`rounded-md ${tint(depth + 1)}`} data-id="element-4956" data-mp="3lq1oxcz6hrc2" data-mpi={i}>
              <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2" data-id="element-4957" data-mp="3qq66l6btos3v">
                <span className="min-w-0 truncate text-[13px] text-muted" data-id="element-4958" data-mp="3vpslrlb7oifb">
                  <span className="text-ink" data-id="element-4959" data-mp="2y221alx5yh1p">{i + 1}.</span> {titleOf(v) || 'Nieuw'}
                </span>
                {controls(i)}
              </div>
              <div className="p-4" data-id="element-4960" data-mp="3314tv6vyiuss">
                <ContentEditor value={v} onChange={next => set(i, next)} depth={depth + 1} data-id="element-4961" data-mp="37zy5suvlidqu" />
              </div>
            </li>)}
        </ul>}
      <button type="button" onClick={() => onChange([...value, template])} className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-2.5 text-[14px] text-clay transition-colors duration-150 ease-soft hover:bg-sand sm:py-1.5 sm:text-[13px]" data-id="element-4962" data-mp="3crzgwdh11yfa">
        <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4963" data-mp="2cpjln0w0acyc" />
        Toevoegen
        {label && <span className="sr-only" data-id="element-4964" data-mp="27sb0crfk9ssv">: {label}</span>}
      </button>
    </Field>;
}